import React, { useState, useEffect, useContext } from "react";
import DatatableComponent from "../../helpers/DatatableComponent";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Snackbar,
  Alert,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import axios from "axios";
import BootstrapDialogTitle from "../../helpers/BootstrapDialogTitle";
import moment from "moment";
import SocketContext from "helpers/SocketContext";
import StatusLayout from "components/Status";
import { successToast, errorToast } from "../../helpers/Notification";
import FormDialog from "../../helpers/FormDialog";
import secureLocalStorage from "react-secure-storage";

function VehicleRequests({ systemSettings }) {
  const mainBackground = systemSettings?.mainBackground;

  // Helper to get local date string in YYYY-MM-DD format
  const getLocalDateString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [bookingData, setBookingData] = useState({
    dropOffDate: getLocalDateString(),
    dropOffFrom: "",
    dropOffTo: "",
    pickUpFrom: "",
    pickUpTo: "",
    incityDestination: "",
    upcountryDestination: "",
    incityReason: "",
    upcountryReason: "",
    vehicleId: "",
  });
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelVehicleId, setCancelVehicleId] = useState(null);
  const [renderKey, setRenderKey] = useState(0);
  const [timeConflict, setTimeConflict] = useState(false);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [existingBookings, setExistingBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [timeRangeError, setTimeRangeError] = useState(null);
  const [dateError, setDateError] = useState(null);
  const [activeTab, setActiveTab] = useState("Incity");
  const [notifying, setNotifying] = useState(false);
  const socket = useContext(SocketContext);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setTimeRangeError(null);
  };

  useEffect(() => {
    const fetchLoggedInUser = async () => {
      setUserLoading(true);
      try {
        const response = await axios.get("/api/request/requests/info");

        if (response.data) {
          setLoggedInUser(response.data);
        }
      } finally {
        setUserLoading(false);
      }
    };

    fetchLoggedInUser();
  }, [count]);

  useEffect(() => {
    const today = getLocalDateString();
    setBookingData((prev) => ({ ...prev, dropOffDate: today }));
  }, []);

  const fetchPendingRequests = async () => {
    try {
      if (loggedInUser && loggedInUser.resource && loggedInUser.resource.id) {
        const response = await axios.get(`/api/vehicle-request/list`);
        if (response.data && response.data.data) {
          const userPendingRequests = response.data.data.filter(
            (req) =>
              req.requestedBy === loggedInUser.resource.id &&
              req.status === "Pending"
          );
          setPendingRequests(userPendingRequests);
        }
      }
    } catch (error) { }
  };

  useEffect(() => {
    if (loggedInUser) {
      fetchPendingRequests();
    }
  }, [loggedInUser, count]);

  useEffect(() => {
    if (!socket) return;
    socket.on("vehicle_request_notification", (data) => {
      if (data.type === "vehicle_requested") {
        successToast(`New vehicle request from ${data.requesterName}`);
      }
    });

    return () => {
      if (socket) socket.off("vehicle_request_notification");
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    // Add null check
    const handleNotification = (data) => {
      switch (data.type) {
        case "vehicle_request_created":
          successToast(data.message);
          break;
        case "vehicle_request_approved":
          successToast(data.message);
          if (data.userId === loggedInUser?.resource?.id) {
            fetchPendingRequests();
          }
          break;
        case "vehicle_request_rejected":
          errorToast(data.message);
          if (data.userId === loggedInUser?.resource?.id) {
            fetchPendingRequests();
          }
          break;
        default:
          break;
      }
    };

    socket.on("getNotification", handleNotification);

    return () => {
      if (socket) socket.off("getNotification", handleNotification);
    };
  }, [socket, loggedInUser]);

  const handleBookNow = async () => {
    setNotifying(true);
    setLoading(true);
    try {
      if (
        !loggedInUser ||
        !loggedInUser.resource ||
        !loggedInUser.resource.id
      ) {
        setError("Please log in to book a vehicle.");
        setLoading(false);
        return;
      }

      if (activeTab === "Incity") {
        if (!bookingData.pickUpFrom) {
          setError("Pickup time is required for Incity bookings.");
          setLoading(false);
          return;
        }
      } else if (activeTab === "Upcountry") {
        if (!bookingData.pickUpFrom) {
          setError("Pickup time is required for Upcountry bookings.");
          setLoading(false);
          return;
        }
        const now = new Date();
        if (
          bookingData.dropOffFrom &&
          new Date(bookingData.dropOffFrom) < now
        ) {
          setError("Drop-off 'from' time cannot be in the past.");
          setLoading(false);
          return;
        }
        if (new Date(bookingData.pickUpFrom) < now) {
          setError("Pick-up 'from' time cannot be in the past.");
          setLoading(false);
          return;
        }
        if (
          bookingData.dropOffFrom &&
          new Date(bookingData.pickUpFrom) <= new Date(bookingData.dropOffFrom)
        ) {
          setError("Pick-up 'from' time must be after drop-off 'from' time.");
          setLoading(false);
          return;
        }
      }

      let dropOffFromDate = null,
        dropOffToDate = null,
        pickUpFromDate = null,
        pickUpToDate = null;

      if (activeTab === "Incity") {
        if (bookingData.dropOffFrom) {
          dropOffFromDate = `${bookingData.dropOffDate}T${bookingData.dropOffFrom}`;
        }
        if (bookingData.dropOffTo) {
          dropOffToDate = `${bookingData.dropOffDate}T${bookingData.dropOffTo}`;
        }
        if (bookingData.pickUpFrom) {
          pickUpFromDate = `${bookingData.dropOffDate}T${bookingData.pickUpFrom}`;
        }
        if (bookingData.pickUpTo) {
          pickUpToDate = `${bookingData.dropOffDate}T${bookingData.pickUpTo}`;
        }
      } else if (activeTab === "Upcountry") {
        pickUpFromDate = bookingData.pickUpFrom || null;
        dropOffFromDate = bookingData.dropOffFrom || null;
      }

      const requestData = {
        requestedBy: loggedInUser.resource.id,
        destination:
          activeTab === "Incity"
            ? bookingData.incityDestination
            : bookingData.upcountryDestination,
        dropOffFrom: dropOffFromDate,
        dropOffTo: dropOffToDate,
        pickUpFrom: pickUpFromDate,
        pickUpTo: pickUpToDate,
        status: "Pending",
        reason:
          activeTab === "Incity"
            ? bookingData.incityReason
            : bookingData.upcountryReason,
        type: activeTab,
      };

      const response = await axios.post(
        `/api/vehicle-request/create/${bookingData.vehicleId}`,
        requestData
      );
      console.log("Booking response:", response);

      if (!response.data.success) {
        errorToast(response.data.message || "Failed to book the vehicle.");
        return;
      }

      successToast("Vehicle booked successfully!");
      setOpenDialog(false);
      setPendingRequests((prev) => [...prev, response.data.data]);
      setCount((prevCount) => prevCount + 1);

      setBookingData({
        dropOffDate: getLocalDateString(),
        dropOffFrom: "",
        dropOffTo: "",
        pickUpFrom: "",
        pickUpTo: "",
        incityDestination: "",
        upcountryDestination: "",
        incityReason: "",
        upcountryReason: "",
        vehicleId: "",
      });
    } catch (error) {
      // console.log("error: ", error)
      errorToast(error.response?.data.error || error.response?.data.message || "Failed to book the vehicle.");
    } finally {
      setLoading(false);
      setNotifying(false);
    }
  };

  const handleCloseSuccessAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const handleCancelRequest = async () => {
    if (!cancelVehicleId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(
        `/api/vehicle-request/cancel/${cancelVehicleId}`
      );
      if (response.data && response.data.success) {
        successToast("Request canceled successfully!");
        setOpenSuccessAlert(true);

        setCount((prevCount) => prevCount + 1);

        await refreshVehicleStatuses();

        if (response.data.vehicleId) {
          try {
            const vehicleResponse = await axios.get(
              `/api/vehicle/details/${response.data.vehicleId}`
            );
            if (vehicleResponse.data && vehicleResponse.data.success) {
              // console.log(
              //   "Vehicle status updated:",
              //   vehicleResponse.data.data.status
              // );
            }
          } catch (err) {
            console.error("Error fetching updated vehicle status:", err);
          }
        }
      }
    } catch (error) {
      setError("Failed to cancel request. Please try again.");
    } finally {
      setLoading(false);
      setOpenCancelDialog(false);
      setCancelVehicleId(null);
    }
  };

  const handleOpenCancelDialog = (requestId) => {
    setCancelVehicleId(requestId);
    setOpenCancelDialog(true);
  };

  const handleOpenBookingDialog = async (vehicleId) => {
    try {
      const vehicleResponse = await fetchVehicleDetails(vehicleId);
      if (vehicleResponse) {
        setVehicleDetails(vehicleResponse);
      }

      setBookingData({
        dropOffDate: getLocalDateString(),
        dropOffFrom: "",
        dropOffTo: "",
        pickUpFrom: "",
        pickUpTo: "",
        incityDestination: "",
        upcountryDestination: "",
        incityReason: "",
        upcountryReason: "",
        vehicleId: vehicleId,
      });

      await fetchExistingBookings(vehicleId);

      setOpenDialog(true);
    } catch (error) { }
  };

  useEffect(() => {
    setRenderKey((prev) => prev + 1);
  }, [pendingRequests, count]);

  const columns = [
    {
      name: "plateNumber",
      label: "Plate Number",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "model",
      label: "Model",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "type",
      label: "Type",
      options: {
        filter: true,
        sort: true,
      },
    },

    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => <StatusLayout status={value} />,
      },
    },

    {
      name: "id",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const vehicleStatus = tableMeta.rowData[3];

          if (vehicleStatus === "Maintenance") {
            return null;
          }

          const pendingRequest = pendingRequests.find(
            (req) => req.vehicleId === v
          );
          const isPending = !!pendingRequest;

          return (
            <>
              {isPending ? (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleOpenCancelDialog(pendingRequest.id)}
                  sx={{
                    minWidth: "120px",
                    maxWidth: "120px",
                    width: "120px",
                    fontSize: "12px",
                    textAlign: "center",
                    borderRadius: "10px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#b71c1c",
                    },
                  }}
                >
                  <>Cancel Request</>
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={
                    !loggedInUser ||
                    !loggedInUser.resource ||
                    userLoading ||
                    vehicleStatus === "Booked"
                  }
                  onClick={() => handleOpenBookingDialog(v)}
                  sx={{
                    minWidth: "120px",
                    textAlign: "center",
                    borderRadius: "10px",
                    backgroundColor: "#336767",
                    "&:hover": {
                      backgroundColor: "#264d4d",
                    },
                  }}
                >
                  {userLoading ? (
                    "Loading..."
                  ) : vehicleStatus === "Booked" ? (
                    "Currently Booked"
                  ) : (
                    <>&nbsp;Book</>
                  )}
                </Button>
              )}
            </>
          );
        },
      },
    },
  ];

  const options = {
    pagination: true,
    rowsPerPage: 20,
    serverSide: true,
    count: -1, // Let the server handle the total count
    onTableChange: (action, tableState) => {
      if (action === "changePage" || action === "changeRowsPerPage") {
        setCount((prev) => prev + 1);
      }
    },
  };

  const fetchExistingBookings = async (vehicleId) => {
    setBookingsLoading(true);
    try {
      setExistingBookings([]);
      setVehicleDetails(null);

      const response = await axios.get(
        `/api/vehicle-request/vehicle/${vehicleId}`
      );

      if (response.data && response.data.success && response.data.data) {
        const sortedBookings = response.data.data.sort(
          (a, b) => new Date(a.from) - new Date(b.from)
        );

        setExistingBookings(sortedBookings);

        if (sortedBookings.length > 0 && sortedBookings[0].Vehicle) {
          setVehicleDetails(sortedBookings[0].Vehicle);
        } else {
          const vehicleResponse = await fetchVehicleDetails(vehicleId);
          setVehicleDetails(vehicleResponse);
        }
      } else {
        const vehicleResponse = await fetchVehicleDetails(vehicleId);
        setVehicleDetails(vehicleResponse);
        setExistingBookings([]);
      }
    } catch (error) {
      setExistingBookings([]);
      const vehicleResponse = await fetchVehicleDetails(vehicleId);
      setVehicleDetails(vehicleResponse);
    } finally {
      setBookingsLoading(false);
    }
  };

  const fetchVehicleDetails = async (vehicleId) => {
    try {
      const response = await axios.get(`/api/vehicle/${vehicleId}`);
      if (response.data && response.data.data) {
        setVehicleDetails(response.data.data);
        return response.data.data;
      } else {
        setError(null);
      }
    } catch (error) { }
    return null;
  };

  const refreshVehicleStatuses = async () => {
    try {
      // console.log("Manually triggering vehicle status refresh...");
      await axios.post("/api/vehicle/refresh-statuses");
      // console.log("Vehicle statuses refresh triggered");

      setCount((prevCount) => prevCount + 1);
    } catch (error) { }
  };

  const isWithin30Minutes = (from, to) => {
    if (!from || !to) return true;
    const fromDate = new Date(`1970-01-01T${from}`);
    const toDate = new Date(`1970-01-01T${to}`);
    const diffMs = toDate - fromDate;
    const diffMins = diffMs / (1000 * 60);
    return diffMins > 0 && diffMins <= 30;
  };

  const isWithin24Hours = (from, to) => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMs = toDate - fromDate;
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours <= 24;
  };

  const isDateInPast = (date) => {
    const today = new Date();
    const selectedDate = new Date(date);
    return selectedDate < today.setHours(0, 0, 0, 0);
  };

  const isTimeInPast = (time) => {
    if (!time) return false;
    const now = new Date();
    const selectedTime = new Date(`${bookingData.dropOffDate}T${time}`);
    const oneMinuteAgo = new Date(now.getTime() - 60000); // 1 minute = 60000 milliseconds
    
    const today = getLocalDateString();
    if (bookingData.dropOffDate === today) {
      return selectedTime < oneMinuteAgo;
    }
    
    return false;
  };

  const isTimeConflicting = (newFrom, newTo) => {
    if (!newFrom || !newTo) return false;

    const newFromTime = new Date(newFrom);
    const newToTime = new Date(newTo);

    if (newFromTime >= newToTime) {
      return "End time must be after start time";
    }

    for (const booking of existingBookings) {
      if (booking.status === "Cancelled" || booking.status === "Denied") {
        continue;//tp35trcgtw
      }

      const bookingFrom = new Date(booking.from);
      const bookingTo = new Date(booking.to);

      if (newFromTime < bookingTo && newToTime > bookingFrom) {
        return `This time conflicts with an existing booking (${moment(
          bookingFrom
        ).format("LT")}-${moment(bookingTo).format("LT")})`;
      }
    }

    return false;
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setBookingData((prev) => ({ ...prev, [field]: value }));

    if (error) {
      setError(null);
    }

    if (field === "dropOffDate") {
      setDateError(null);
    }

    if (
      field === "dropOffFrom" &&
      bookingData.dropOffTo &&
      value >= bookingData.dropOffTo
    ) {
      setTimeRangeError("Drop-off 'From' time must be earlier than 'To' time.");
    } else if (
      field === "dropOffTo" &&
      bookingData.dropOffFrom &&
      value <= bookingData.dropOffFrom
    ) {
      setTimeRangeError("Drop-off 'To' time must be later than 'From' time.");
    } else if (
      field === "pickUpFrom" &&
      bookingData.pickUpTo &&
      value >= bookingData.pickUpTo
    ) {
      setTimeRangeError("Pick-up 'From' time must be earlier than 'To' time.");
    } else if (
      field === "pickUpTo" &&
      bookingData.pickUpFrom &&
      value <= bookingData.pickUpFrom
    ) {
      setTimeRangeError("Pick-up 'To' time must be later than 'From' time.");
    } else {
      setTimeRangeError(null);
    }

    if (
      field === "dropOffFrom" ||
      field === "dropOffTo" ||
      field === "pickUpFrom" ||
      field === "pickUpTo"
    ) {
      const dropOffFromDate = new Date(
        `${bookingData.dropOffDate}T${bookingData.dropOffFrom}`
      );
      const dropOffToDate = new Date(
        `${bookingData.dropOffDate}T${bookingData.dropOffTo}`
      );
      const pickUpFromDate = new Date(
        `${bookingData.dropOffDate}T${bookingData.pickUpFrom}`
      );
      const pickUpToDate = new Date(
        `${bookingData.dropOffDate}T${bookingData.pickUpTo}`
      );

      if (
        isTimeConflicting(dropOffFromDate, dropOffToDate) ||
        isTimeConflicting(pickUpFromDate, pickUpToDate)
      ) {
        // setTimeRangeError(
        //   "The selected time conflicts with an existing booking."
        // );
      } 
      else {
        setTimeRangeError(null);
      }
    }
  };
  useEffect(() => {
    let isMounted = true;

    const doRefresh = async () => {
      if (isMounted) {
        await refreshVehicleStatuses();
      }
    };

    doRefresh();

    const intervalId = setInterval(() => {
      if (isMounted) {
        doRefresh();
      }
    }, 2 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const handleManualRefresh = async () => {
    try {
      setLoading(true);
      await refreshVehicleStatuses();
      setSuccessMessage("Vehicle statuses refreshed successfully!");
      setOpenSuccessAlert(true);
    } catch (error) {
      setError("Failed to refresh vehicle statuses.");
    } finally {
      setLoading(false);
    }
  };

  const getUpcountryPickUpFromMin = () => {
    if (!bookingData.dropOffFrom) return "";
    return bookingData.dropOffFrom;
  };

  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const handleRowClick = async (rowData, rowMeta) => {
    const vehicleId = rowData[rowData.length - 1]; // Get the ID from the last column
    try {
      const response = await axios.get(
        `/api/vehicle-request/vehicle/${vehicleId}`
      );
      if (response.data && response.data.success) {
        const userBookings = response.data.data.filter(
          (booking) => booking.requestedBy === loggedInUser?.resource?.id
        );
        if (userBookings.length > 0) {
          setSelectedBooking(userBookings[0]);
          setDetailsDialog(true);
        }
      }
    } catch (error) {
      console.error("Error fetching booking details:", error);
    }
  };

  // Add this dialog component
  const BookingDetailsDialog = () => (
    <Dialog
      open={detailsDialog}
      onClose={() => setDetailsDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <BootstrapDialogTitle onClose={() => setDetailsDialog(false)}>
        Booking Details
      </BootstrapDialogTitle>
      <DialogContent>
        {selectedBooking && (
          <div className="space-y-4">
            <Typography>
              <strong>Vehicle:</strong> {selectedBooking.Vehicle?.plateNumber} (
              {selectedBooking.Vehicle?.model})
            </Typography>
            <Typography>
              <strong>Status:</strong>{" "}
              <StatusLayout status={selectedBooking.status} />
            </Typography>
            <Typography>
              <strong>Destination:</strong> {selectedBooking.destination}
            </Typography>
            <Typography>
              <strong>Type:</strong> {selectedBooking.type}
            </Typography>
            <Typography>
              <strong>Drop-off Time:</strong>{" "}
              {moment(selectedBooking.dropOffFrom).format("YYYY-MM-DD HH:mm")} -{" "}
              {moment(selectedBooking.dropOffTo).format("HH:mm")}
            </Typography>
            <Typography>
              <strong>Pick-up Time:</strong>{" "}
              {moment(selectedBooking.pickUpFrom).format("YYYY-MM-DD HH:mm")} -{" "}
              {moment(selectedBooking.pickUpTo).format("HH:mm")}
            </Typography>
            <Typography>
              <strong>Reason:</strong> {selectedBooking.reason}
            </Typography>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="w-full mb-12 px-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <BootstrapDialogTitle onClose={() => setOpenDialog(false)}>
          {vehicleDetails
            ? `Booking Vehicle: ${vehicleDetails.plateNumber} - ${vehicleDetails.model}`
            : "Booking Vehicle"}
        </BootstrapDialogTitle>

        <DialogContent>
          {bookingsLoading ? (
            <div className="flex justify-center my-8">
              <CircularProgress />
            </div>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {existingBookings.length > 0 && (
                <div className="mb-4">
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This vehicle has existing bookings. Please select a time
                    that doesn't conflict with these bookings
                  </Alert>
                  <TableContainer component={Paper} className="mt-3 mb-4">
                    <Table size="small" aria-label="booking table">
                      <TableHead style={{ backgroundColor: mainBackground }}>
                        <TableRow>
                          <TableCell
                            style={{ color: "white", fontWeight: "bold" }}
                          >
                            Booked By
                          </TableCell>
                          <TableCell
                            style={{ color: "white", fontWeight: "bold" }}
                          >
                            Drop-off From
                          </TableCell>
                          <TableCell
                            style={{ color: "white", fontWeight: "bold" }}
                          >
                            Drop-off To
                          </TableCell>
                          <TableCell
                            style={{ color: "white", fontWeight: "bold" }}
                          >
                            Pick-up From
                          </TableCell>
                          <TableCell
                            style={{ color: "white", fontWeight: "bold" }}
                          >
                            Pick-up To
                          </TableCell>
                          <TableCell
                            style={{ color: "white", fontWeight: "bold" }}
                          >
                            Destination
                          </TableCell>
                          <TableCell
                            style={{ color: "white", fontWeight: "bold" }}
                          >
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {existingBookings.map((booking, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {booking.Requester?.names || "Unknown"}
                            </TableCell>
                            <TableCell>
                              {booking.dropOffFrom ? (
                                <>
                                  {moment(booking.dropOffFrom).format("MM/DD")}
                                  <br />
                                  {moment(booking.dropOffFrom).format("HH:mm")}
                                </>
                              ) : (
                                "N/A"
                              )}
                            </TableCell>
                            <TableCell>
                              {booking.dropOffTo ? (
                                <>
                                  {moment(booking.dropOffTo).format("MM/DD")}
                                  <br />
                                  {moment(booking.dropOffTo).format("HH:mm")}
                                </>
                              ) : (
                                "N/A"
                              )}
                            </TableCell>
                            <TableCell>
                              {booking.pickUpFrom ? (
                                <>
                                  {moment(booking.pickUpFrom).format("MM/DD")}
                                  <br />
                                  {moment(booking.pickUpFrom).format("HH:mm")}
                                </>
                              ) : (
                                "N/A"
                              )}
                            </TableCell>
                            <TableCell>
                              {booking.pickUpTo ? (
                                <>
                                  {moment(booking.pickUpTo).format("MM/DD")}
                                  <br />
                                  {moment(booking.pickUpTo).format("HH:mm")}
                                </>
                              ) : (
                                "N/A"
                              )}
                            </TableCell>
                            <TableCell>
                              {booking.destination || "N/A"}
                            </TableCell>
                            <TableCell>
                              <StatusLayout status={booking.status} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}

              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                  sx={{
                    "& .MuiFormControlLabel-root": {
                      marginRight: "16px",
                    },
                    "& .MuiRadio-root": {
                      display: "none",
                    },
                  }}
                >
                  <FormControlLabel
                    value="Incity"
                    control={<Radio />}
                    label="Incity"
                    sx={{
                      backgroundColor:
                        activeTab === "Incity" ? mainBackground : "transparent",
                      color: activeTab === "Incity" ? "white" : "black",
                      borderRadius: "4px",
                      padding: "8px 16px",
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor:
                          activeTab === "Incity" ? "#285252" : "#f0f0f0",
                      },
                    }}
                  />
                  <FormControlLabel
                    value="Upcountry"
                    control={<Radio />}
                    label="Upcountry"
                    sx={{
                      backgroundColor:
                        activeTab === "Upcountry"
                          ? mainBackground
                          : "transparent",
                      color: activeTab === "Upcountry" ? "white" : "black",
                      borderRadius: "4px",
                      padding: "8px 16px",
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor:
                          activeTab === "Upcountry" ? "#285252" : "#f0f0f0",
                      },
                    }}
                  />
                </RadioGroup>
              </FormControl>

              {activeTab === "Incity" && (
                <div>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Incity Bookings can be scheduled for any future date
                  </Alert>
                  <>
                    <TextField
                      label="Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={bookingData.dropOffDate}
                      onChange={handleInputChange("dropOffDate")}
                      className="mb-4"
                      margin="normal"
                      required
                      error={
                        !!dateError || isDateInPast(bookingData.dropOffDate)
                      }
                      helperText={
                        isDateInPast(bookingData.dropOffDate)
                          ? "Date cannot be in the past."
                          : dateError
                      }
                      inputProps={{
                        min: getLocalDateString(),
                      }}
                    />
                    <Typography variant="subtitle1" gutterBottom>
                      Drop-off Time
                    </Typography>
                    <div className="flex gap-4">
                      <TextField
                        label="From"
                        type="time"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={bookingData.dropOffFrom}
                        onChange={handleInputChange("dropOffFrom")}
                        className="mb-4"
                        margin="normal"
                        error={
                          !!timeRangeError ||
                          isTimeInPast(bookingData.dropOffFrom)
                        }
                        helperText={
                          isTimeInPast(bookingData.dropOffFrom)
                            ? "Time cannot be in the past."
                            : timeRangeError
                        }
                      />
                      <TextField
                        label="To"
                        type="time"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={bookingData.dropOffTo}
                        onChange={handleInputChange("dropOffTo")}
                        className="mb-4"
                        margin="normal"
                        error={!!timeRangeError}
                        helperText={timeRangeError}
                      />
                    </div>
                    <Typography variant="subtitle1" gutterBottom>
                      Pick-up Time
                    </Typography>
                    <div className="flex gap-4">
                      <TextField
                        label="From"
                        type="time"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={bookingData.pickUpFrom}
                        onChange={handleInputChange("pickUpFrom")}
                        className="mb-4"
                        margin="normal"
                        required
                        error={!!timeRangeError}
                        helperText={timeRangeError}
                      />
                      <TextField
                        label="To"
                        type="time"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={bookingData.pickUpTo}
                        onChange={handleInputChange("pickUpTo")}
                        className="mb-4"
                        margin="normal"
                        error={!!timeRangeError}
                        helperText={timeRangeError}
                      />
                    </div>
                    <TextField
                      label="Destination"
                      fullWidth
                      value={bookingData.incityDestination}
                      onChange={handleInputChange("incityDestination")}
                      className="mb-4"
                      margin="normal"
                      required
                    />
                    <TextField
                      label="Reason"
                      fullWidth
                      multiline
                      rows={4}
                      value={bookingData.incityReason}
                      onChange={handleInputChange("incityReason")}
                      className="mb-4"
                      margin="normal"
                      required
                    />
                  </>
                </div>
              )}

              {activeTab === "Upcountry" && (
                <>
                  <TextField
                    label="Drop-off From"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={bookingData.dropOffFrom}
                    onChange={handleInputChange("dropOffFrom")}
                    className="mb-4"
                    margin="normal"
                    error={!!error && error.includes("Drop-off")}
                    helperText={
                      error && error.includes("Drop-off") ? error : ""
                    }
                    inputProps={{
                      min: new Date().toISOString().slice(0, 16),
                    }}
                  />
                  <TextField
                    label="Pick-up From"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={bookingData.pickUpFrom}
                    onChange={handleInputChange("pickUpFrom")}
                    className="mb-4"
                    margin="normal"
                    required
                    error={
                      !!error &&
                      (error.includes("Pick-up") ||
                        error.includes("time must be after"))
                    }
                    helperText={
                      error &&
                        (error.includes("Pick-up") ||
                          error.includes("time must be after"))
                        ? error
                        : ""
                    }
                    inputProps={{
                      min:
                        bookingData.dropOffFrom ||
                        new Date().toISOString().slice(0, 16),
                    }}
                  />
                  <TextField
                    label="Destination"
                    fullWidth
                    value={bookingData.upcountryDestination}
                    onChange={handleInputChange("upcountryDestination")}
                    className="mb-4"
                    margin="normal"
                    required
                  />
                  <TextField
                    label="Reason"
                    fullWidth
                    multiline
                    rows={4}
                    value={bookingData.upcountryReason}
                    onChange={handleInputChange("upcountryReason")}
                    className="mb-4"
                    margin="normal"
                    required
                  />
                </>
              )}
            </>
          )}
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => setOpenDialog(false)}
              color="primary"
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBookNow}
              variant="contained"
              sx={{
                backgroundColor: "#336767",
                color: "white",
                "&:hover": {
                  backgroundColor: "#264d4d",
                },
              }}
              disabled={
                loading ||
                bookingsLoading ||
                !!timeRangeError ||
                (activeTab === "Incity" &&
                  (!bookingData.pickUpFrom ||
                    !bookingData.incityDestination ||
                    !bookingData.incityReason)) ||
                (activeTab === "Upcountry" &&
                  (!bookingData.pickUpFrom ||
                    !bookingData.upcountryDestination ||
                    !bookingData.upcountryReason))
              }
            >
              {loading ? "Booking..." : "Book Now"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <BootstrapDialogTitle onClose={() => setOpenCancelDialog(false)}>
          Cancel Request
        </BootstrapDialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this vehicle booking request?
          </Typography>
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => setOpenCancelDialog(false)}
              color="primary"
              className="mr-2"
              disabled={loading}
            >
              No
            </Button>
            <Button
              onClick={handleCancelRequest}
              variant="contained"
              color="error"
              disabled={loading}
            >
              {loading ? "Cancelling..." : "Yes, Cancel"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DatatableComponent
        key={`datatable-${count}-${renderKey}`}
        processData={(data) => {
          if (data && data.data) {
            const filteredData = data.data.filter(
              (vehicle) => vehicle.status !== "Maintenance"
            );
            return { data: filteredData };
          }
          return { data: [] };
        }}
        elevation={1}
        searchfield={true}
        search={false}
        viewColumns={false}
        columns={columns}
        url={`/api/vehicle/list?count=${count}`}
        title="Request Vehicle"
        forceReload={renderKey}
        options={{
          ...options,
          onRowClick: (rowData, rowMeta) => {
            const vehicleStatus = rowData[3]; // Status column index
            const vehicleId = rowData[rowData.length - 1];
            const hasPendingRequest = pendingRequests.some(
              (req) => req.vehicleId === vehicleId
            );

            // Only allow row click if user has a booking for this vehicle
            if (hasPendingRequest) {
              handleRowClick(rowData, rowMeta);
            }
          },
        }}
      />
      <BookingDetailsDialog />
    </div>
  );
}

export default VehicleRequests;
